/**
 * UI store (06 §9 uiStore) — ephemeral interface state: which left-pane overlay
 * is open (new chat/group/settings), info slide-over, media viewer, toasts.
 * Route-backed overlays are mirrored here for convenience; deep state lives in
 * the URL via react-router (06 §2.4).
 */

import { create } from 'zustand';
import { ulid } from '../data/ids';

export type LeftOverlay =
  | { kind: 'none' }
  | { kind: 'new-chat' }
  | { kind: 'new-group-members'; selected: string[] }
  | { kind: 'new-group-details'; selected: string[] }
  | { kind: 'settings'; page: SettingsPage }
  | { kind: 'profile' };

export type SettingsPage =
  | 'root'
  | 'profile'
  | 'privacy'
  | 'notifications'
  | 'appearance'
  | 'security'
  | 'devices';

export interface Toast {
  id: string;
  text: string;
}

interface UiState {
  leftOverlay: LeftOverlay;
  /** Info slide-over open for this conversation id, or null. */
  infoForConv: string | null;
  /** Media viewer open for this message clientMsgId, or null. */
  mediaForMessage: string | null;
  /** In-chat search open. */
  searchOpen: boolean;
  toasts: Toast[];

  setLeftOverlay: (o: LeftOverlay) => void;
  closeLeftOverlay: () => void;
  openInfo: (cid: string) => void;
  closeInfo: () => void;
  openMedia: (clientMsgId: string) => void;
  closeMedia: () => void;
  setSearchOpen: (open: boolean) => void;
  toast: (text: string) => void;
  dismissToast: (id: string) => void;
}

export const useUiStore = create<UiState>((set) => ({
  leftOverlay: { kind: 'none' },
  infoForConv: null,
  mediaForMessage: null,
  searchOpen: false,
  toasts: [],

  setLeftOverlay: (leftOverlay) => set({ leftOverlay }),
  closeLeftOverlay: () => set({ leftOverlay: { kind: 'none' } }),
  openInfo: (cid) => set({ infoForConv: cid }),
  closeInfo: () => set({ infoForConv: null }),
  openMedia: (clientMsgId) => set({ mediaForMessage: clientMsgId }),
  closeMedia: () => set({ mediaForMessage: null }),
  setSearchOpen: (searchOpen) => set({ searchOpen }),

  toast: (text) => {
    const id = ulid();
    set((s) => ({ toasts: [...s.toasts, { id, text }] }));
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, 2600);
  },
  dismissToast: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));
